import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr, inrShort } from "@/lib/format";
import { toast } from "sonner";
import { Send, CreditCard, X, Check, Clock, AlertTriangle } from "lucide-react";

const STATUS_TONE = {
  draft: "bg-anvi-100 text-anvi-700",
  sent: "bg-blue-50 text-blue-700",
  partially_paid: "bg-amber-50 text-amber-800",
  paid: "bg-emerald-50 text-emerald-700",
  overdue: "bg-red-50 text-red-700",
};
const STATUS_ICON = {
  paid: Check, partially_paid: Clock, overdue: AlertTriangle, sent: Send, draft: null,
};
const STATUS_LABEL = {
  draft: "Draft", sent: "Sent", partially_paid: "Partially Paid", paid: "Paid", overdue: "Overdue",
};

function InvoiceDetail({ invoice, payments, onClose, onAction }) {
  const outstanding = invoice.total - (invoice.collected || 0);
  const isInterState = invoice.igst > 0;

  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-3xl border border-anvi-200 shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="invoice-detail-modal">
        <div className="flex items-start justify-between px-6 py-5 border-b border-anvi-200">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Invoice</div>
            <div className="font-heading text-2xl text-anvi-900 mt-1">{invoice.invoice_number}</div>
            <div className="text-sm text-anvi-500 mt-1">{invoice.client_name} · {invoice.milestone_label}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] px-2.5 py-1 rounded-full uppercase tracking-wider ${STATUS_TONE[invoice.status]}`}>{STATUS_LABEL[invoice.status]}</span>
            <button onClick={onClose} className="text-anvi-500 hover:text-anvi-900 p-1"><X size={18} /></button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 mb-1">From</div>
              <div className="text-sm font-medium text-anvi-900">Anvi Signature Spaces</div>
              <div className="text-sm text-anvi-500">Nagpur, {invoice.tenant_state}</div>
              <div className="text-xs text-anvi-500 mt-1">GSTIN: 27ANVIP1234R1Z5</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 mb-1">To</div>
              <div className="text-sm font-medium text-anvi-900">{invoice.client_name}</div>
              <div className="text-sm text-anvi-500">{invoice.client_state}</div>
              <div className="text-xs text-anvi-500 mt-1">Due: {new Date(invoice.due_date).toLocaleDateString("en-IN")}</div>
            </div>
          </div>

          <div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 border-b border-anvi-200">
                  <th className="text-left py-2">Description</th>
                  <th className="text-left py-2">HSN</th>
                  <th className="text-right py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items.map((li, i) => (
                  <tr key={`${li.hsn_code}-${li.description}-${i}`} className="border-b border-anvi-100">
                    <td className="py-2.5 text-anvi-900">{li.description}</td>
                    <td className="py-2.5 text-anvi-500 tabular-nums">{li.hsn_code}</td>
                    <td className="py-2.5 text-right tabular-nums text-anvi-900">{inr(li.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-anvi-50 rounded-md p-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-anvi-500">Subtotal</span><span className="tabular-nums text-anvi-900">{inr(invoice.subtotal)}</span></div>
            {isInterState ? (
              <div className="flex justify-between"><span className="text-anvi-500">IGST (18%)</span><span className="tabular-nums text-anvi-900" data-testid="inv-igst">{inr(invoice.igst)}</span></div>
            ) : (
              <>
                <div className="flex justify-between"><span className="text-anvi-500">CGST (9%)</span><span className="tabular-nums text-anvi-900" data-testid="inv-cgst">{inr(invoice.cgst)}</span></div>
                <div className="flex justify-between"><span className="text-anvi-500">SGST (9%)</span><span className="tabular-nums text-anvi-900" data-testid="inv-sgst">{inr(invoice.sgst)}</span></div>
              </>
            )}
            <div className="flex justify-between pt-2 border-t border-anvi-200">
              <span className="font-medium text-anvi-900">Total</span>
              <span className="font-heading text-xl tabular-nums text-anvi-900" data-testid="inv-total">{inr(invoice.total)}</span>
            </div>
            <div className="flex justify-between text-xs pt-1">
              <span className="text-anvi-500">Collected</span>
              <span className="tabular-nums text-emerald-700">{inr(invoice.collected)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-anvi-500">Outstanding</span>
              <span className={`tabular-nums ${outstanding > 0 ? "text-red-700" : "text-anvi-400"}`}>{inr(outstanding)}</span>
            </div>
          </div>

          {payments.length > 0 && (
            <div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 mb-2">Payments received</div>
              <div className="space-y-1.5">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-xs border border-anvi-100 rounded-md px-3 py-2">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-anvi-500">{p.razorpay_payment_id}</span>
                      <span className="text-anvi-500">via {p.method}</span>
                    </div>
                    <span className="tabular-nums text-emerald-700 font-medium">{inr(p.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-anvi-200">
            {invoice.status === "draft" && (
              <button onClick={() => onAction("send", invoice.id)} data-testid="invoice-send-btn"
                      className="flex items-center gap-2 bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
                <Send size={14} strokeWidth={1.5} /> Send to client
              </button>
            )}
            {outstanding > 0 && invoice.status !== "draft" && (
              <button onClick={() => onAction("pay", invoice.id)} data-testid="invoice-mockpay-btn"
                      className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
                <CreditCard size={14} strokeWidth={1.5} /> Simulate payment (mock Razorpay)
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [payments, setPayments] = useState([]);

  const load = () => api.invoicesList().then((r) => setInvoices(r.data));
  useEffect(() => { load(); }, []);

  const openInvoice = async (id) => {
    const r = await api.invoice(id);
    setSelected(r.data.invoice);
    setPayments(r.data.payments);
  };

  const handleAction = async (action, id) => {
    try {
      if (action === "send") {
        await api.invoiceSend(id);
        toast.success("Invoice sent · Payment link (mock Razorpay) generated");
      } else if (action === "pay") {
        const r = await api.invoiceMockPay(id, null);
        toast.success(`₹${Math.round(r.data.amount_paid).toLocaleString("en-IN")} received · status: ${r.data.status}`);
      }
      await load();
      const r2 = await api.invoice(id);
      setSelected(r2.data.invoice);
      setPayments(r2.data.payments);
    } catch (err) {
      toast.error("Action failed");
    }
  };

  const totals = invoices.reduce((a, i) => ({
    invoiced: a.invoiced + i.total,
    collected: a.collected + (i.collected || 0),
    overdue: a.overdue + (i.status === "overdue" ? i.total - (i.collected || 0) : 0),
  }), { invoiced: 0, collected: 0, overdue: 0 });

  return (
    <div className="px-10 pt-4 pb-10 animate-fade-in" data-testid="invoices-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-anvi-500 mb-2">Accounts</div>
          <h1 className="font-heading text-4xl text-anvi-900">Invoices</h1>
          <p className="text-sm text-anvi-500 mt-2">{invoices.length} invoices · GST-compliant · Razorpay payment links (mocked in demo)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-anvi-200 rounded-md p-5">
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Invoiced</div>
          <div className="font-heading text-3xl text-anvi-900 tabular-nums mt-1">{inrShort(totals.invoiced)}</div>
        </div>
        <div className="bg-white border border-anvi-200 rounded-md p-5">
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Collected</div>
          <div className="font-heading text-3xl text-emerald-700 tabular-nums mt-1">{inrShort(totals.collected)}</div>
        </div>
        <div className="bg-white border border-anvi-200 rounded-md p-5">
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Overdue</div>
          <div className="font-heading text-3xl text-red-700 tabular-nums mt-1">{inrShort(totals.overdue)}</div>
        </div>
      </div>

      <div className="bg-white border border-anvi-200 rounded-md overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 border-b border-anvi-200 bg-anvi-50/50">
              <th className="px-4 py-3 text-left font-medium">Invoice #</th>
              <th className="px-4 py-3 text-left font-medium">Client</th>
              <th className="px-4 py-3 text-left font-medium">Milestone</th>
              <th className="px-4 py-3 text-right font-medium">Total</th>
              <th className="px-4 py-3 text-right font-medium">Collected</th>
              <th className="px-4 py-3 text-left font-medium">Due</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((i) => {
              const Icon = STATUS_ICON[i.status];
              return (
                <tr key={i.id} onClick={() => openInvoice(i.id)} data-testid={`invoice-row-${i.id}`}
                    className="border-b border-anvi-100 hover:bg-anvi-50 cursor-pointer transition-colors">
                  <td className="px-4 py-3.5 font-mono text-xs text-anvi-900">{i.invoice_number}</td>
                  <td className="px-4 py-3.5 text-sm text-anvi-900">{i.client_name}</td>
                  <td className="px-4 py-3.5 text-sm text-anvi-500">{i.milestone_label || "—"}</td>
                  <td className="px-4 py-3.5 text-sm text-right font-medium tabular-nums text-anvi-900">{inr(i.total)}</td>
                  <td className="px-4 py-3.5 text-sm text-right tabular-nums text-anvi-600">{inr(i.collected || 0)}</td>
                  <td className="px-4 py-3.5 text-xs text-anvi-500 tabular-nums">{new Date(i.due_date).toLocaleDateString("en-IN")}</td>
                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_TONE[i.status]}`}>
                      {Icon && <Icon size={10} strokeWidth={2} />} {STATUS_LABEL[i.status]}
                    </span>
                  </td>
                </tr>
              );
            })}
            {invoices.length === 0 && (
              <tr><td colSpan={7} className="text-center py-16 text-sm text-anvi-500">No invoices yet · raise one from an approved estimate</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && <InvoiceDetail invoice={selected} payments={payments} onClose={() => setSelected(null)} onAction={handleAction} />}
    </div>
  );
}
